import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import { Box, List, Skeleton, Stack } from '@mui/material';
import SportsIcon from '../../../atoms/SportsIcon';
import NeobetSidebarListItem from './NeobetSidebarListItem';
import type { BettingDrawerData } from './useBettingDrawerData';
import { useSidebarPresentation } from '../contexts/SidebarPresentationContext';

const styles = {
  list: {
    py: 0.25,
  },
  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: 1.5,
    py: 1,
  },
  skeletonIcon: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    flexShrink: 0,
  },
  skeletonText: {
    flex: 1,
    height: 22,
  },
} as const;

export const BettingDrawerLoadingState = () => (
  <Stack>
    {Array.from({ length: 15 }).map((_, index) => (
      <Box key={index} sx={styles.skeletonRow}>
        <Skeleton variant='circular' sx={styles.skeletonIcon} />
        <Skeleton variant='text' sx={styles.skeletonText} />
      </Box>
    ))}
  </Stack>
);

type BettingDrawerListProps = {
  drawerData: BettingDrawerData;
};

export const CasinoDrawerList = ({ drawerData }: BettingDrawerListProps) => {
  const { closeDrawer } = useSidebarPresentation();
  const {
    navigate,
    t,
    selectedCategoryId,
    visibleCategories,
    showShowAllCategories,
    showShowLessCategories,
    setShowAllCategories,
  } = drawerData;
  const casinoLabel = t('casino');
  const showAllLabel = t('showAll', 'Show All');
  const showLessLabel = t('showLess', 'Show Less');

  return (
    <List sx={styles.list}>
      {visibleCategories.map((category) => {
        const isSelected = selectedCategoryId === category._id;

        return (
          <NeobetSidebarListItem
            key={category._id}
            label={category.name || category.providerCategoryName || casinoLabel}
            isSelected={isSelected}
            icon={null}
            onClick={() => {
              navigate(isSelected ? '/casino' : `/casino?category=${category._id}`);
              closeDrawer();
            }}
          />
        );
      })}
      {showShowAllCategories ? (
        <NeobetSidebarListItem label={showAllLabel} onClick={() => setShowAllCategories(true)} />
      ) : null}
      {showShowLessCategories ? (
        <NeobetSidebarListItem label={showLessLabel} onClick={() => setShowAllCategories(false)} />
      ) : null}
    </List>
  );
};

export const SportsbookDrawerList = ({ drawerData }: BettingDrawerListProps) => {
  const { navigate, t, selectedSportId, visibleSports, showShowAllSports, showShowLessSports, setShowAllSports } =
    drawerData;
  const showAllLabel = t('showAll', 'Show All');
  const showLessLabel = t('showLess', 'Show Less');

  return (
    <List sx={styles.list}>
      {visibleSports.map((sport) => {
        const isSelected = selectedSportId === sport.id;

        return (
          <NeobetSidebarListItem
            key={sport.id}
            label={sport.name}
            isSelected={isSelected}
            icon={
              sport.icon ? <SportsIcon name={sport.icon} size={18} /> : <SportsSoccerRoundedIcon fontSize='small' />
            }
            onClick={() => navigate(`/sports/${sport.id}`, { state: { name: sport.name } })}
          />
        );
      })}
      {showShowAllSports ? <NeobetSidebarListItem label={showAllLabel} onClick={() => setShowAllSports(true)} /> : null}
      {showShowLessSports ? (
        <NeobetSidebarListItem label={showLessLabel} onClick={() => setShowAllSports(false)} />
      ) : null}
    </List>
  );
};
